import React, { useState, useEffect, useImperativeHandle, useMemo, useCallback, ReactNode, useRef, forwardRef, useContext } from "react";
import classnames from 'classnames';
import './UserDefaultSelections.scss';
import { useUser } from "@/contexts/UserContext";
import { ByzzerChip } from "@/components/ByzzerChip";
import { nanoid } from "nanoid";
import { ByzzerDate } from "@byzzer/ui-components";
import { CustomTimePeriod, ReportDatatype, RunConfigMarket, TimePeriod, WeekEndingTimePeriod } from "@/types/ReportRun";
import { isMarketDisabled, useMarketService } from "@/services/market.service";
import { MarketPickerContext } from "@/components/MarketPicker";
import { omniProductLevelOptions } from "@/constants/omniProducts.constants";

// Helper function to get visible fields for each dataset
const getDatasetVisibleFields = (dataset?: ReportDatatype): string[] => {
    const dataset_type = dataset || 'rms';
    
    switch (dataset_type) {
        case 'rms':
            return ['brands', 'categories', 'characteristics', 'markets', 'timePeriod', 'focusKPI'];
        case 'cps':
            return ['brands', 'categories','subCategories', 'characteristics', 'demographicDimensions', 'markets', 'timePeriod'];
        case 'omni':
            return ['brands', 'categories', 'omniProductLevel', 'markets', 'subMarkets', 'timePeriod'];
        case 'osh':
            return ['categories', 'subCategories', 'characteristics', 'productDimensions', 'demographics', 'markets', 'timePeriod', 'focusKPI'];
        default:
            return ['brands', 'categories', 'characteristics', 'markets', 'timePeriod', 'focusKPI'];
    }
};

export type UserDefaultSelectionsProps = {
    onEditModeClick: () => void;
    selectedDataset?: ReportDatatype;
}

type DefaultSelectionType = {
    type: ReactNode;
    selections: ReactNode;
    baseClassName?: string;
}

function isWeekEndingTimePeriod(obj: any): obj is WeekEndingTimePeriod {
  return  typeof obj?.period?.period?.weeks === 'number' &&
           obj?.period?.type==='week_ending'
}
const renderTimePeriodChip = (tp?: TimePeriod) => { // TODO: See if this is standardized somewhere.  I thought it was.. DRY
    let returnVal;
    if (!tp ) return '';
    if(isWeekEndingTimePeriod(tp)){
        tp.type='week_ending'
    }
    if (['week_ending', 'omni_custom', 'cps_custom'].includes(tp.type) && !(tp as CustomTimePeriod).period?.endDate) {
        return 'N/a';
    }

    if (tp.type === 'week_ending') {
        returnVal = 
        <>
            {`${tp.period?.weeks ?? 0} week${tp.period?.weeks! > 1 ? 's' : ''} ending `}
            <ByzzerDate date={tp.period?.endDate!} format={'yyyy-MM-dd'} timeZone={'America/New_York'} />
        </> 
    }else if( tp.type === 'omni_custom' || tp.type === 'cps_custom'){               
        returnVal = 
        <>
            {`${tp.period?.months ?? 0} month${tp.period?.months! > 1 ? 's' : ''} ending `}
            <ByzzerDate date={tp.period?.endDate!} format={'yyyy-MM-dd'} timeZone={'America/New_York'} />
        </>
    }else {
        returnVal = tp?.period;
    }
    return <ByzzerChip>{returnVal}</ByzzerChip>;
}

function formatOshDemographicChipLabel(demo: any): string {
    if (!demo) return '';
    if (demo?.demographic) {
        const values = Array.isArray(demo.value)
            ? demo.value.join(', ')
            : demo?.value
            ? String(demo.value)
            : '';
        return `${demo.demographic} ${demo.condition ?? 'is'} ${values}`.trim();
    }
    if (typeof demo === 'string') return demo;
    return demo?.display ?? demo?.value ?? '';
}

function DefaultSelection({type, selections, baseClassName}: DefaultSelectionType) {
    return (
        <div className={classnames(`${baseClassName}__group`)}>
            <div className={classnames(`${baseClassName}__type`)}>
                <span>{type}</span>
            </div>
            <div className={classnames(`${baseClassName}__selections`)}>
                {selections}
            </div>
        </div>
    )
}

export const UserDefaultSelections = ({
    onEditModeClick,
    selectedDataset,
    ...props
}: UserDefaultSelectionsProps) => {

    const {
        defaultRunConfig,
        features: { enableLimitedMarketSubscription },
        accessibleMasterCompanies,
        company,
    } = useUser();
    const { getCachedMarketNodeByName } = useMarketService();
    const { requiredMasterCompany, requiredMarketGroup, requireRemainingMarket } = useContext(MarketPickerContext);
        
    // Get the config for the selected dataset
    const currentDatasetConfig = defaultRunConfig?.[selectedDataset || 'rms'];
    
    const {
        brands = [],
        timePeriod,
        markets = [],
        categories = [],
        subcategories = [],
        demographicDimensions = [],
        characteristics = [],
        focusKPI
    } = currentDatasetConfig ?? {};

    const [enabledMarkets, setEnabledMarkets] = useState(markets);

    const baseClassName = 'byz-default-selections-section';

    useEffect(() => {
        if (markets.length) {
            handleEnabledMarkets();
        }
    }, [markets]);

    function handleEnabledMarkets() {
        const modifiedDefaultMarkets = markets?.reduce((acc: RunConfigMarket[], item) => {
            const disabledMarketNode = isMarketDisabled(getCachedMarketNodeByName(item.name, categories)!, {
                requiredMarketGroup,
                requiredMasterCompany,
                requireRemainingMarket,
                enableLimitedMarketSubscription,
                accessibleMasterCompanies,
                purchasedMarketKeys: company?.purchasedMarketKeys,
            });
            if (!disabledMarketNode) {
                acc.push({ ...item as RunConfigMarket });
            }
            return acc;
        }, []);
        setEnabledMarkets(modifiedDefaultMarkets);
    }

    function handleEditModeClick() {
        onEditModeClick();
    }

    const actions = useMemo(() => (
        <div className={classnames(`${baseClassName}__actions`)}>
            <div className={`${baseClassName}__edit-container`} onClick={handleEditModeClick}>
                <span className={classnames(`${baseClassName}__edit-btn`)} />
                <span className={classnames(`${baseClassName}__edit-text`)}>Edit</span>
            </div>
        </div>
    ), [])

    const visibleFields = useMemo(() => getDatasetVisibleFields(selectedDataset), [selectedDataset]);

    const allSelections: Record<string, DefaultSelectionType> = useMemo(() => {
        const omniFocusProducts = currentDatasetConfig?.omniFocusProducts ?? [];
        const brandSelections = selectedDataset === 'omni' ? omniFocusProducts : brands;

        return {
        brands: {
            type: 'Focus Brand(s)',
            selections: Boolean(brandSelections?.length) ? (
                <>{brandSelections?.map((brand: string) => <ByzzerChip key={brand} label={brand}/>)}</>
            ) : (
                <>(None)</>
            )
        },
        categories: {
            type: 'Categories',
            selections: Boolean(categories?.length) ? (
                <>{categories?.map(category => <ByzzerChip key={category} label={category}/>)}</>
            ) : (
                <>(None)</>
            )
        },
        subCategories: {
            type: 'Subcategories',
            selections: Boolean(subcategories?.length) ? (
                <>{subcategories?.map(subcategory => <ByzzerChip key={subcategory} label={subcategory}/>)}</>
            ) : (
                <>(None)</>
            )
        },
        characteristics: {
            type: 'Characteristics',
            selections: Boolean(characteristics?.length) ? (
                <>{characteristics?.map(characteristic => (
                    <ByzzerChip key={nanoid()}>
                        {characteristic?.characteristicDisplayValue} {characteristic?.condition} {characteristic?.value?.join(', ')}
                    </ByzzerChip>
                ))}</>
            ) : (
                <>(None)</>
            )
        },
        demographicDimensions: {
            type: 'Demographic Dimensions',
            selections: Boolean(demographicDimensions?.length) ? (
                <>{demographicDimensions?.map(dimension => <ByzzerChip key={dimension.display} label={dimension.display}/>)}</>
            ) : (
                <>(None)</>
            )
        },
        markets: {
            type: selectedDataset === 'omni' ? 'Total Market' : 'Market',
            selections: Boolean(currentDatasetConfig?.markets?.length) ? (
                <>{currentDatasetConfig?.markets?.map(market => <ByzzerChip key={market.name} label={market.name}/>)}</>
            ) : (
                <>(None)</>
            )
        },
        omniProductLevel: {
            type: 'Product Level',
            selections: currentDatasetConfig?.omniProductLevel ? (
                <ByzzerChip label={omniProductLevelOptions.find((option) => option.value === currentDatasetConfig.omniProductLevel)?.display ?? currentDatasetConfig.omniProductLevel}/>
            ) : (
                <>(None)</>
            )
        },
        subMarkets: {
            type: 'Submarket',
            selections: Boolean(currentDatasetConfig?.subMarkets?.length) ? (
                <>{currentDatasetConfig?.subMarkets?.map((sm: RunConfigMarket) => <ByzzerChip key={sm.name} label={sm.name}/>)}</>
            ) : (
                <>(None)</>
            )
        },
        productDimensions: {
            type: 'Product Dimension',
            selections: Boolean(currentDatasetConfig?.productDimensions?.length) ? (
                <>{currentDatasetConfig?.productDimensions?.map(characteristic => (
                    <ByzzerChip key={nanoid()}>
                        {characteristic?.characteristic}
                    </ByzzerChip>
                ))}</>
            ) : (
                <>(None)</>
            )
        },
        timePeriod: {
            type: 'Time Period',
            selections: Boolean(timePeriod?.period) ? (
                <>{renderTimePeriodChip(timePeriod) ?? <>(None)</>}</>
            ) : (
                <>(None)</>
            )
        },
        focusKPI: {
            type: 'Focus KPI',
            selections: Boolean(focusKPI?.length) ? (
                <><ByzzerChip key={nanoid()} label={focusKPI} /></>
            ) : (
                <>(None)</> 
            )
        },
        demographics: {
            type: 'Demographics',
            selections: Boolean(currentDatasetConfig?.demographics?.length) ? (
                <>{currentDatasetConfig?.demographics?.map((demo, index) => (
                    <ByzzerChip key={`${formatOshDemographicChipLabel(demo)}-${index}`}>
                        {formatOshDemographicChipLabel(demo)}
                    </ByzzerChip>
                ))}</>
            ) : (
                <>(None)</>
            )
        }
    }}, [selectedDataset, currentDatasetConfig]);

    const defaultSelections: DefaultSelectionType[] = useMemo(() => 
        visibleFields.map(field => allSelections[field]).filter(Boolean),
        [visibleFields, allSelections]
    );

    return (
        <div className={classnames(`${baseClassName}`)}>
            <header className={classnames(`${baseClassName}__header`)}>
                <span className={classnames(`${baseClassName}__name`)}>
                    Default Selections
                </span>
                {actions}
            </header>
            {/* <section className={classnames(`${baseClassName}__description`)}></section> For future use */}
            <div className={classnames(`${baseClassName}__groups`)}>
                {defaultSelections.map(({type, selections}) => (
                    <DefaultSelection key={nanoid()} type={type} selections={selections} baseClassName={baseClassName}/>
                ))}
            </div>
        </div>
    );

};

export default UserDefaultSelections;

UserDefaultSelections.displayName = 'UserDefaultSelections';
